import { ChangeDetectionStrategy, Component, OnInit, TemplateRef, computed, inject, signal, viewChild } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { HlmButtonDirective } from '@spartan-ng/helm/button';
import { HlmSwitchComponent } from '@spartan-ng/helm/switch';
import { HlmTooltipTriggerDirective } from '@spartan-ng/helm/tooltip';
import { BackgroundExecutionService } from '@core/services/background-execution.service';
import { ColumnDef, DataTableComponent } from '@shared/components/data-table/data-table';
import { InlineNotice } from '@shared/components/inline-notice/inline-notice';
import { StepTimeline } from '@shared/components/step-timeline/step-timeline';
import { TimelineStep } from '@shared/models/step-timeline.model';
import { ConfirmationDialogService } from '@shared/services/confirmation-dialog.service';
import { CampaignWorkflowStore } from '../../store/campaign-workflow.store';
import { BulkRunHistoryItem } from '../../services/campaigns.service';
import { STAGE_META, WORKFLOW_STAGES, runDuration, runKind, runStatus, runSummary, runTimeline } from '../../models/campaign-workflow.model';

const fmt = (n: number) => n.toLocaleString('de-DE');
/** The technical state of the whole campaign, how to start the next run, and every run so far. */
@Component({
  selector: 'app-campaign-executions-tab',
  standalone: true,
  imports: [DatePipe, DecimalPipe, HlmButtonDirective, HlmSwitchComponent, HlmTooltipTriggerDirective, DataTableComponent, InlineNotice, StepTimeline],
  templateUrl: './campaign-executions-tab.html',
  host: { class: 'flex flex-col gap-6' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CampaignExecutionsTab implements OnInit {
  readonly store = inject(CampaignWorkflowStore);
  private readonly background = inject(BackgroundExecutionService);
  private readonly confirm = inject(ConfirmationDialogService);

  readonly technical = signal(false);
  readonly runTpl = viewChild.required<TemplateRef<unknown>>('runTpl');
  readonly resultTpl = viewChild.required<TemplateRef<unknown>>('resultTpl');
  readonly statusTpl = viewChild.required<TemplateRef<unknown>>('statusTpl');
  readonly actionTpl = viewChild.required<TemplateRef<unknown>>('actionTpl');

  readonly hoveredLast = signal<number | null>(null);
  readonly timelineConfig = { variant: 'rail', showLines: true, showDescriptions: true, orientation: 'horizontal', animated: true, interactive: true } as const;
  readonly steps = computed(() =>
    runTimeline(this.store.items() ?? [], this.technical(), { hoveredLast: this.hoveredLast(), clickable: this.store.canStart() && !this.store.running() }),
  );

  readonly columns = computed<ColumnDef<BulkRunHistoryItem>[]>(() => [
    { header: 'Berechnung', template: this.runTpl(), sortValue: (run) => run.createdAt, searchValue: (run) => this.kind(run) },
    { header: 'Analysen', cell: (run) => fmt(run.total), class: 'w-[88px] text-right font-mono', sortValue: (run) => run.total },
    { header: 'Ergebnis', template: this.resultTpl() },
    { header: 'Stand', template: this.statusTpl(), class: 'w-[132px]', sortValue: (run) => run.status },
    { header: '', template: this.actionTpl(), class: 'w-[120px] text-right' },
  ]);

  /** The active run's live counters over its history row. */
  readonly displayRuns = computed<BulkRunHistoryItem[]>(() => {
    const job = this.background.bulkJobs().get(this.store.campaignId() ?? '');
    const rows = this.store.runs();
    if (!job) return rows;
    return rows.map((run) =>
      run.runId === job.runId
        ? { ...run, status: job.phase === 'running' ? 'running' : run.status, total: job.total || run.total, dispatched: job.dispatched, succeeded: job.completed ?? run.succeeded, failed: job.failed + (job.pipelineFailed ?? 0), skipped: job.skipped }
        : run,
    );
  });

  readonly getRunId = (run: BulkRunHistoryItem) => run.runId;

  ngOnInit(): void {
    void this.store.loadRuns();
    void this.store.loadItems();
    void this.store.loadRunStages();
  }

  onHover(step: TimelineStep | null): void {
    this.hoveredLast.set(step && step.id !== 'created' ? Number(step.id) : null);
  }

  runTo(step: TimelineStep): void {
    if (step.id === 'created') return;
    const to = Number(step.id);
    const counts = this.store.counts();
    this.store.openRun({ to, target: to >= 4 && !counts?.new ? 'replan' : 'new' });
  }

  async cancel(): Promise<void> {
    const ok = await this.confirm.confirm({
      title: 'Berechnung abbrechen?',
      description: 'Es wird nichts Neues mehr gestartet, laufende Berechnungen werden gestoppt. Fertige Ergebnisse bleiben.',
      confirmText: 'Abbrechen',
      cancelText: 'Weiterlaufen lassen',
      type: 'danger',
    });
    if (ok) await this.store.cancelRun();
  }

  readonly kind = runKind;
  readonly duration = runDuration;
  readonly status = runStatus;
  readonly summary = runSummary;

  /** Where the analyses of this execution stand now, in the order of the phases. */
  stagesOf(run: BulkRunHistoryItem) {
    const counts = this.store.runStages()?.[run.runId] ?? {};
    return WORKFLOW_STAGES.flatMap((stage) => {
      const count = counts[stage] ?? 0;
      return count ? [{ stage, count, label: STAGE_META[stage].label, color: STAGE_META[stage].color }] : [];
    });
  }

  summaryOf(run: BulkRunHistoryItem): string {
    const parts = this.stagesOf(run);
    return parts.length ? parts.map((part) => `${fmt(part.count)} ${part.label}`).join(' · ') : runSummary(run);
  }

  /** What makes sense to do with a finished run. */
  next(run: BulkRunHistoryItem): { label: string; go: () => void } | null {
    if (run.status === 'running' || run.status === 'pending') return null;
    if (run.failed && this.store.counts()?.error)
      return { label: 'Fehler erneut →', go: () => this.store.openRun({ target: 'error' }) };
    if (run.action === 'plan') return { label: 'Nachkontrolle →', go: () => void this.store.go('review', { stage: 'recheck', runId: run.runId }) };
    if (run.endTask >= 3 && run.dispatched) return { label: 'Prüfen →', go: () => void this.store.go('review', { stage: 'review', runId: run.runId }) };
    return { label: 'Analysen →', go: () => void this.store.go('analyses', { runId: run.runId }) };
  }
}
